/**
 * AdminAppApkCard — Admin Panel card for uploading / managing the BeeWeddy APK.
 *
 * The uploaded APK is served by the backend `/api/download/app` endpoint with
 * `Content-Disposition: attachment` so the public Download App button on the
 * Create Account page triggers an actual file save.
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CloudArrowUp, Trash, FileArrowDown } from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtSize = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const AdminAppApkCard = () => {
  const [meta, setMeta] = useState(null);
  const [version, setVersion] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/app-apk`, { withCredentials: true });
      setMeta(data);
      if (data?.version) setVersion(data.version);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load APK status');
    }
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.apk')) {
      toast.error('Please choose a .apk file');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    if (version.trim()) fd.append('version', version.trim());
    setUploading(true);
    setProgress(0);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/app-apk`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      setMeta(data);
      toast.success('APK uploaded — Download App button is live.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove the current APK? The Download App button will hide until you upload a new one.')) return;
    try {
      const { data } = await axios.delete(`${BACKEND_URL}/api/admin/app-apk`, { withCredentials: true });
      setMeta(data);
      toast.success('APK removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-app-apk-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl">App APK Download</CardTitle>
        <CardDescription>
          Upload the BeeWeddy Android APK. Visitors will see a <strong>Download App</strong> button at the top of the Create Account page; clicking it streams this file as a direct download.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {meta?.available ? (
          <div className="flex flex-wrap items-center gap-3 p-3 border border-green-300 bg-green-50 rounded" data-testid="apk-status-available">
            <FileArrowDown size={22} weight="fill" className="text-green-700" />
            <div className="flex-1 min-w-0 text-sm">
              <div className="font-bold text-green-900 truncate">{meta.filename || 'beeweddy.apk'}</div>
              <div className="text-xs text-green-800">
                {fmtSize(meta.size_bytes)}
                {meta.version ? ` · v${meta.version}` : ''}
                {meta.uploaded_at ? ` · uploaded ${new Date(meta.uploaded_at).toLocaleString()}` : ''}
              </div>
            </div>
            <a
              href={`${BACKEND_URL}/api/download/app`}
              className="text-xs underline text-green-800"
              data-testid="apk-test-download-link"
            >
              Test download
            </a>
            <Button
              variant="outline"
              onClick={handleDelete}
              className="border-red-300 text-red-700 hover:bg-red-50 rounded-none h-8"
              data-testid="apk-delete-btn"
            >
              <Trash size={14} className="mr-1" /> Remove
            </Button>
          </div>
        ) : (
          <div className="p-3 border border-dashed border-[#E8E1D5] rounded text-sm text-text-secondary" data-testid="apk-status-empty">
            No APK uploaded yet — the public Download App button is hidden until you upload one.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
          <div className="space-y-1">
            <Label className="text-xs font-bold text-text-secondary">Version label (optional)</Label>
            <Input
              type="text"
              placeholder="e.g. 1.2.0"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              className="rounded-none border-[#E8E1D5] h-9 font-mono"
              data-testid="apk-version-input"
            />
            <p className="text-[10px] text-text-secondary">Displayed alongside the APK in this admin card.</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-bold text-text-secondary">Upload {meta?.available ? 'new' : ''} APK</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              onChange={handleUpload}
              disabled={uploading}
              className="block w-full text-xs file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-brand-primary file:text-sand-50 file:rounded-none file:font-bold hover:file:bg-[#A84A38] disabled:opacity-50"
              data-testid="apk-file-input"
            />
            {uploading && (
              <div className="mt-1">
                <div className="h-1 bg-sand-100 rounded overflow-hidden">
                  <div className="h-full bg-brand-primary transition-all" style={{ width: `${progress}%` }} />
                </div>
                <div className="text-[10px] text-text-secondary mt-1">{progress}% uploaded…</div>
              </div>
            )}
          </div>
        </div>

        <p className="text-[11px] text-text-secondary border-l-2 border-brand-primary/30 pl-2 flex items-start gap-1">
          <CloudArrowUp size={14} className="flex-shrink-0 mt-[1px]" />
          <span>Uploading a new APK instantly replaces the previous one. The public Download endpoint always serves the latest file you uploaded here.</span>
        </p>
      </CardContent>
    </Card>
  );
};

export default AdminAppApkCard;
